import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Search,
  Eye,
  MapPin
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Button, LoadingSpinner } from './components';

function LocationsPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [sortField, setSortField] = useState('revenue_last_3_months');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    apiService.get('/auth/profile/').then(u => setUserRole(u.role)).catch(() => {});
  }, []);
  const itemsPerPage = 8;

  // Fetch locations from API on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getLocations();
        // Handle paginated response
        setLocations(data.results || data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch locations');
        console.error('Error fetching locations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(locations)) return [];

    let filtered = locations.filter(location => {
      const matchesSearch = !searchTerm ||
        (location.name && location.name.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'technician_count':
          aValue = a.technician_count || 0;
          bValue = b.technician_count || 0;
          break;
        case 'client_count':
          aValue = a.client_count || 0;
          bValue = b.client_count || 0;
          break;
        case 'recent_client_count':
          aValue = a.recent_client_count || 0;
          bValue = b.recent_client_count || 0;
          break;
        case 'revenue_last_3_months':
          aValue = a.revenue_last_3_months || 0;
          bValue = b.revenue_last_3_months || 0;
          break;
        case 'avg_weekly_appointments':
          aValue = a.avg_weekly_appointments || 0;
          bValue = b.avg_weekly_appointments || 0;
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }

      if (sortDirection === 'asc') {
        return typeof aValue === 'string' ? aValue.localeCompare(bValue) : aValue - bValue;
      } else {
        return typeof aValue === 'string' ? bValue.localeCompare(aValue) : bValue - aValue;
      }
    });

    return filtered;
  }, [locations, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900">LOCATIONS</h1>
        </div>
        <div className="h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900">LOCATIONS</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <div className="text-red-800 text-xs font-semibold mb-2">Error Loading Locations</div>
          <p className="text-red-600 text-xs mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-800 text-white px-3 py-1 text-xs rounded-lg hover:bg-red-900 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="mb-3">
        <h1 className="text-lg font-semibold text-gray-900">LOCATIONS</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
            />
          </div>
          <button className="bg-red-800 text-white px-3 py-1 text-xs rounded hover:bg-red-900 transition-colors flex items-center space-x-1">
            <Plus className="w-3.5 h-3.5" />
            <span>Add Location</span>
          </button>
        </div>
      </div>

      {/* Locations Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>LOCATION</span>
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('technician_count')}
                >
                  <div className="flex items-center space-x-1">
                    <span>TEAM</span>
                    {sortField === 'technician_count' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('client_count')}
                >
                  <div className="flex items-center space-x-1">
                    <span>TOTAL CLIENTS</span>
                    {sortField === 'client_count' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('recent_client_count')}
                >
                  <div className="flex items-center space-x-1">
                    <span>CLIENTS (3M)</span>
                    {sortField === 'recent_client_count' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                {userRole !== 'coordinator' && <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('revenue_last_3_months')}
                >
                  <div className="flex items-center space-x-1">
                    <span>REVENUE (3M)</span>
                    {sortField === 'revenue_last_3_months' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>}
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('avg_weekly_appointments')}
                >
                  <div className="flex items-center space-x-1">
                    <span>AVG WEEKLY APPTS</span>
                    {sortField === 'avg_weekly_appointments' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-2 py-8 text-center text-xs text-gray-500">
                    No locations found
                  </td>
                </tr>
              ) : (
                currentData.map((location) => (
                  <tr key={location.id} className="hover:bg-neutral-50">
                    <td className="px-2 py-1.5 text-xs">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-3.5 h-3.5 text-red-800 flex-shrink-0" />
                        <span className="font-medium text-gray-900">
                          {location.name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900 text-center">
                      {location.technician_count || 0}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900 text-center">
                      {location.client_count || 0}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900 text-center">
                      {location.recent_client_count || 0}
                    </td>
                    {userRole !== 'coordinator' && <td className="px-2 py-1.5 text-xs text-gray-900 font-semibold">
                      {formatCurrency(location.revenue_last_3_months || 0)}
                    </td>}
                    <td className="px-2 py-1.5 text-xs text-gray-900 text-center">
                      {location.avg_weekly_appointments || 0}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-center">
                      <button
                        onClick={() => navigate(`/locations/${location.id}`)}
                        className="text-gray-400 hover:text-red-800 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, filteredAndSortedData.length)}</span> of{' '}
              <span className="font-medium">{filteredAndSortedData.length}</span> results
            </p>
          </div>
          <div className="flex gap-1">
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant={currentPage === 1 ? 'secondary' : 'primary'}
              size="sm"
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  variant={currentPage === pageNum ? 'primary' : 'secondary'}
                  size="sm"
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant={currentPage === totalPages ? 'secondary' : 'primary'}
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <div className="pb-16" />
    </div>
  );
}

export default LocationsPage;
